import { memo, useMemo } from "react";
import { ChatStatus, UIMessage } from "ai";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { BeatLoader } from "react-spinners";

import { Box, Center, Stack, ThemeIcon } from "@mantine/core";

import ChatMessage from "./ChatMessage";
import ChatLoading from "./ChatLoading";

import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";

type ChatConversationProps = {
  messages: UIMessage<{ hidden?: boolean }>[];
  status: ChatStatus;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isError: boolean;
  reachedBottomOnce: boolean;
  onLoadMore: () => void;
};

const ChatConversation = (props: ChatConversationProps) => {
  const {
    messages,
    status,
    isFetchingNextPage,
    hasNextPage,
    isError,
    reachedBottomOnce,
    onLoadMore
  } = props;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    disabled: isError || !reachedBottomOnce,
    onLoadMore
  });

  const { rollups } = useSelectedProject();

  const lastPartType = useMemo(() => {
    const lastMessage = messages.at(-1);

    if (lastMessage) {
      return lastMessage.parts.at(-1)?.type;
    }
  }, [messages]);

  return (
    <Box px="lg" pb="lg" w="100%">
      <Center ref={sentryRef} h={50}>
        {isFetchingNextPage ? (
          <ThemeIcon color="violet">
            <BeatLoader color="var(--mantine-color-violet-6)" size={7} />
          </ThemeIcon>
        ) : null}
      </Center>

      <Stack gap="xl" justify="flex-end">
        {messages.map(message => {
          return (
            <ChatMessage
              key={message.id}
              message={message}
              projects={rollups.projects}
            />
          );
        })}

        {status === "submitted" || status === "streaming" ? (
          <ChatLoading lastPartType={lastPartType} />
        ) : null}
      </Stack>
    </Box>
  );
};

export default memo(ChatConversation);
